import type { TranslationKey } from "../../../../state/i18n";
import { useTranslation } from "../../../../state/i18n";
import type { ParentComponent, VoidComponent } from "solid-js";
import { createMemo, createSignal, createUniqueId, For, getOwner, Match, runWithOwner, Show, Switch } from "solid-js";
import posthog from "posthog-js";
import { isEmpty, isNil } from "lodash-es";
import { A, useNavigate } from "@solidjs/router";
import { createHotkey, type Hotkey } from "@tanstack/solid-hotkeys";
import { useCustomerState } from "../../../../state/customer";
import { debounce } from "@solid-primitives/scheduled";
import type { ItemType, LastEditedForm, RecentlySearched, SearchResult } from "../../../../services/search/types";
import { useGlobalSearch } from "../../../../services/search/useGlobalSearch";
import SeparatorLine from "../../../../components/SeparatorLine";
import type { ListCollection } from "@ark-ui/solid";
import {
  Combobox,
  ComboboxItemContext,
  ComboboxItemGroupLabel,
  createListCollection,
  Dialog,
  useAsyncList,
  useCombobox,
} from "@ark-ui/solid";
import Portal from "../../../../utils/Portal";
import { Icon } from "../../../../components/icon/Icon";
import { TextButton } from "../../../../components/button/Button";
import type { InputValueChangeDetails } from "@zag-js/combobox";
import { Kbd } from "../../../../components/kbd/Kbd.tsx";
import { KbdHint } from "../../../../components/kbd/KbdHint.tsx";
import { useModalManager } from "../../../../components/modal/ModalManager.tsx";
import { keyFromSelector } from "i18next";
import { createFormSearchResult } from "../../../../services/search/providers/FormSearchResult.tsx";
import GenericFailureModal from "../../../GenericFailureModal.tsx";
import { useLastEditedForms } from "./useLastEditedForms.ts";
import { POSTHOG_EVENTS } from "../../../../services/PostHogService";

const DEBOUNCE_DELAY = 300;
const OPEN_GLOBAL_SEARCH_HOTKEY: Hotkey = "Mod+K";

const ITEM_TYPE_TRANSLATION_KEYS: Record<ItemType, TranslationKey> = {
  RECENTLY_SEARCHED: keyFromSelector(($) => $.GLOBAL_SEARCH_TYPE_RECENTLY_SEARCHED_LABEL),
  LAST_EDITED_FORM: keyFromSelector(($) => $.GLOBAL_SEARCH_TYPE_LAST_EDITED_FORM_LABEL),
  FOLDER: keyFromSelector(($) => $.GLOBAL_SEARCH_TYPE_FOLDER_LABEL),
  FORM: keyFromSelector(($) => $.GLOBAL_SEARCH_TYPE_FORM_LABEL),
  DATA_SOURCE: keyFromSelector(($) => $.GLOBAL_SEARCH_TYPE_DATA_SOURCE_LABEL),
  GROUP: keyFromSelector(($) => $.GLOBAL_SEARCH_TYPE_GROUP_LABEL),
  USER: keyFromSelector(($) => $.GLOBAL_SEARCH_TYPE_USER_LABEL),
  PAGE: keyFromSelector(($) => $.GLOBAL_SEARCH_TYPE_PAGE_LABEL),
};

const GlobalSearch = () => {
  const { t } = useTranslation();
  const { hasModalOpen } = useModalManager();
  const [isSearchActive, setIsSearchActive] = createSignal(false);

  createHotkey(
    OPEN_GLOBAL_SEARCH_HOTKEY,
    () => setIsSearchActive(true),
    () => ({
      enabled: !hasModalOpen(),
    }),
  );

  return (
    <div>
      <div class="w-135">
        <button
          onClick={() => setIsSearchActive(true)}
          class="flex w-full items-center gap-1.5 rounded-sm border border-subtle bg-surface-default py-2 pr-3 pl-2 text-base-medium text-secondary hover:bg-surface-hover focus-visible:outline-default active:bg-surface-pressed"
        >
          <Icon class="icon-tertiary" name="search" type="solid" size="md" />
          <div>{t(($) => $.GLOBAL_SEARCH_INPUT_PLACEHOLDER)}</div>
          <div class="ml-auto">
            <Kbd hotkey={OPEN_GLOBAL_SEARCH_HOTKEY} />
          </div>
        </button>
      </div>

      <Dialog.Root
        open={isSearchActive()}
        onOpenChange={(value) => setIsSearchActive(value.open)}
        lazyMount
        unmountOnExit
        aria-label={t(($) => $.GLOBAL_SEARCH_DIALOG_TITLE)}
      >
        <Portal>
          <Dialog.Backdrop class="fixed inset-0 z-20 block! size-full animate-backdrop-fade-out bg-overlay-default open:animate-backdrop-fade-in" />
          <Dialog.Positioner class="fixed inset-0 z-20 flex items-start justify-center overflow-y-auto pt-20">
            <Dialog.Content>
              <SearchBox close={() => setIsSearchActive(false)} />
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </div>
  );
};

type SearchResultsProps = {
  collection: ListCollection<SearchResult | RecentlySearched | LastEditedForm>;
};
const SearchResults: VoidComponent<SearchResultsProps> = (props) => {
  const { t } = useTranslation();
  const customer = useCustomerState();

  return (
    <div class="divide-y divide-subtle p-1">
      <For each={props.collection.group()}>
        {([type, results]) => (
          <Combobox.ItemGroup class="pb-1">
            <ComboboxItemGroupLabel class="px-3 pt-2.5 pb-0.5 text-header-sm text-secondary">
              {t(ITEM_TYPE_TRANSLATION_KEYS[type as ItemType])}
            </ComboboxItemGroupLabel>
            <For each={results}>
              {(result) => (
                <Combobox.Item
                  item={result}
                  asChild={(itemProps) =>
                    result.actions.Enter.type === "navigate" ? (
                      <A class="group block" href={result.actions.Enter.href(customer.customerId)} {...itemProps()} />
                    ) : (
                      <div class="group" {...itemProps()} />
                    )
                  }
                >
                  <ComboboxItemContext>
                    {(itemContext) => (
                      <div class="group-highlighted:bg-surface-hover">
                        <result.ContentComponent highlighted={itemContext().highlighted} />
                      </div>
                    )}
                  </ComboboxItemContext>
                </Combobox.Item>
              )}
            </For>
          </Combobox.ItemGroup>
        )}
      </For>
    </div>
  );
};

type SearchBoxProps = { close: () => void };
const SearchBox: VoidComponent<SearchBoxProps> = (props) => {
  const owner = getOwner();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open } = useModalManager();
  const customer = useCustomerState();
  const searchService = useGlobalSearch();

  const list = useAsyncList<SearchResult>({
    load: async ({ filterText, signal }) => {
      if (!filterText) {
        return {
          items: [],
        };
      }

      if (signal?.aborted ?? false) {
        return { items: [] };
      }

      const results = await searchService.search(filterText);
      return { items: results };
    },
  });

  const activeLastEditedForms = useLastEditedForms(customer.customerId);

  const collection = createMemo(() => {
    const items = isEmpty(list().filterText)
      ? [
          ...searchService.getRecentlySearched().map((item): RecentlySearched => ({
            type: "RECENTLY_SEARCHED",
            id: createUniqueId(),
            query: item,
            ContentComponent: () => (
              <div class="flex items-center gap-3 px-3 py-2.5">
                <Icon class="mx-2 text-icon-tertiary" name="search" type="solid" size="md" />
                <span class="text-base-medium text-primary">{item}</span>
              </div>
            ),
            actions: {
              Enter: {
                type: "command",
                run: async () => combobox().setInputValue(item),
              },
            },
          })),
          ...activeLastEditedForms().map(({ folder, form }): LastEditedForm => ({
            type: "LAST_EDITED_FORM",
            ...createFormSearchResult({ form, folder, customerId: customer.customerId }),
          })),
        ]
      : list().items;

    return createListCollection<SearchResult | RecentlySearched | LastEditedForm>({
      items,
      itemToValue: ({ id }) => id,
      groupBy: ({ type }) => type,
    });
  });

  const debouncedSearch = debounce(list().setFilterText, DEBOUNCE_DELAY);
  const handleInputChange = (details: InputValueChangeDetails) => {
    if (details.reason === "input-change" || details.reason === "script") {
      debouncedSearch(details.inputValue);
    }
  };

  const combobox = useCombobox({
    open: true,
    get collection() {
      return collection();
    },
    selectionBehavior: "preserve",
    onInputValueChange: handleInputChange,
    onValueChange: ({ value }) => {
      const item = collection().find(value[0]);
      if (isNil(item)) {
        return;
      }

      if (item.type === "RECENTLY_SEARCHED") {
        combobox().setInputValue(item.query);
      } else {
        searchService.addRecentlySearched(list().filterText);
        posthog.capture(POSTHOG_EVENTS.GLOBAL_SEARCH_RESULT_SELECTED, { type: item.type });
      }
    },
    onOpenChange: (details) => {
      const item = collection().find(details.value[0]);
      if (
        details.reason === "item-select" &&
        (isEmpty(details.value) || // Avoid closing when pressing 'Enter' in the `Combobox.Input` without a selection
          item?.type === "RECENTLY_SEARCHED") // Avoid closing when selecting a recently searched item
      ) {
        return;
      }

      if (!details.open) {
        props.close();
        list().clearFilter();
      }
    },
    onInteractOutside: (e) => {
      // Prevents the combobox from closing when clicking around `Combobox.Input`
      e.preventDefault();
    },
    navigate: (a) => {
      // Required to navigating outside Solid Router
      navigate(new URL(a.href).pathname);
    },
  });

  createHotkey("Mod+Enter", async (_, context) => {
    const { highlightedItem } = combobox();
    const action = highlightedItem?.actions?.[context.hotkey];

    runWithOwner(owner, async () => {
      try {
        if (action?.type === "command") {
          await action.run();
        } else if (action?.type === "navigate") {
          const href = await action.href(customer.customerId);
          if (!isNil(href)) {
            posthog.capture(POSTHOG_EVENTS.GLOBAL_SEARCH_RESULT_SELECTED, { type: highlightedItem?.type });
            navigate(href);
          }
        }
      } catch (error) {
        open({ content: () => <GenericFailureModal error={error} /> });
      }
    });
  });

  const resultsStatus = createMemo((): "loading" | "error" | "emptyQuery" | "emptyResults" | "results" => {
    if (list().loading) {
      return "loading";
    }
    if (!isNil(list().error)) {
      return "error";
    }
    if (isEmpty(list().filterText)) {
      return isEmpty(collection().group()) ? "emptyQuery" : "results";
    }

    return isEmpty(list().items) ? "emptyResults" : "results";
  });

  return (
    <Combobox.RootProvider value={combobox}>
      <Combobox.Context>
        {(context) => (
          <div class="z-10 w-2xl rounded-lg bg-surface-default shadow-elevation-light-3">
            <Combobox.Control class="flex h-13.5 items-center gap-1.5 py-3 pr-3 pl-4">
              <Icon class="shrink-0 icon-secondary" name="search" type="solid" size="md" />
              <Combobox.Input
                class="w-full text-base-medium text-primary outline-none placeholder:text-placeholder"
                placeholder={t(($) => $.GLOBAL_SEARCH_INPUT_PLACEHOLDER)}
                onKeyDown={(event) => {
                  if (event.metaKey && event.key === "Enter") {
                    // Ark UI Combobox triggers navigation on 'Enter'. This conflicts with our `Mod+Enter` handling, so
                    // we have to prevent the default Ark UI behavior.
                    event.preventDefault();
                  }
                }}
              />

              <Show when={context().inputValue}>
                <TextButton
                  variant="tertiary"
                  color="brand"
                  size="medium"
                  text={t(($) => $.GLOBAL_SEARCH_INPUT_ACTION_CLEAR_LABEL)}
                  onClick={() => {
                    context().setInputValue("");
                    list().clearFilter();
                  }}
                />
              </Show>
            </Combobox.Control>

            <SeparatorLine />

            <Combobox.Content
              aria-label={t(($) => $.GLOBAL_SEARCH_RESULTS_ARIA_LABEL)}
              // oxlint-disable-next-line better-tailwindcss/no-restricted-classes -- We need the dynamic height to be calculated
              class="max-h-[min(--spacing(160),calc(100vh-(--spacing(64))))] min-h-60 overflow-y-scroll"
            >
              <Switch fallback={<SearchResults collection={collection()} />}>
                <Match when={resultsStatus() === "loading"}>
                  <FakeOptionWrapper>
                    <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
                  </FakeOptionWrapper>
                </Match>
                <Match when={resultsStatus() === "error"}>
                  <FakeOptionWrapper>
                    <p>{t(($) => $.GLOBAL_SEARCH_ERROR)}</p>
                  </FakeOptionWrapper>
                </Match>
                <Match when={resultsStatus() === "emptyQuery"}>
                  <FakeOptionWrapper>
                    <p class="w-66 text-center">{t(($) => $.GLOBAL_SEARCH_EMPTY_QUERY)}</p>
                  </FakeOptionWrapper>
                </Match>
                <Match when={resultsStatus() === "emptyResults"}>
                  <FakeOptionWrapper>
                    <p>{t(($) => $.GLOBAL_SEARCH_EMPTY_RESULTS)}</p>
                  </FakeOptionWrapper>
                </Match>
              </Switch>
            </Combobox.Content>

            <ActionBar hasQuery={!isEmpty(list().filterText)} results={list().items.length} />
          </div>
        )}
      </Combobox.Context>
    </Combobox.RootProvider>
  );
};

const ActionBar: VoidComponent<{ hasQuery: boolean; results: number }> = (props) => {
  const { t } = useTranslation();

  return (
    <div class="flex items-center justify-between rounded-b-lg border-t border-subtle bg-surface-extra-subtle px-4 py-3 pointer-coarse:hidden">
      <div class="flex gap-5">
        <KbdHint hotkeys={["ArrowUp", "ArrowDown"]} label={t(($) => $.GLOBAL_SEARCH_ACTION_BAR_ACTIONS_NAVIGATE)} />
        <KbdHint hotkeys="Enter" label={t(($) => $.GLOBAL_SEARCH_ACTION_BAR_ACTIONS_OPEN)} />
        <KbdHint hotkeys="Escape" label={t(($) => $.GLOBAL_SEARCH_ACTION_BAR_ACTIONS_CLOSE)} />
      </div>

      <Show when={props.hasQuery}>
        <div class="text-small-regular text-secondary" role="status">
          {t(($) => $.GLOBAL_SEARCH_ACTION_BAR_RESULTS_COUNT, { count: props.results })}
        </div>
      </Show>
    </div>
  );
};

const FakeOptionWrapper: ParentComponent = (props) => (
  <div
    class="flex h-60 items-center justify-center text-medium-regular text-secondary"
    role="option"
    aria-selected={false}
    aria-disabled
  >
    {props.children}
  </div>
);

export default GlobalSearch;
